import React from "react";
import Button, { ButtonDesign } from "../../components/Button/Button";
import FieldSet from "../../components/FieldSet/FieldSet";
import Input from "../../components/Input/Input";
import { view } from "../../lib/vvm";
import { Layout } from "../../components/Layout/Layout";
import { Action } from "../../types/entities";
import { JournalPageViewModel } from "./JournalPageViewModel";
import styles from "./JournalPage.module.css";

function formatDate(
  date: {
    day: number;
    month: number;
    year: number;
    hour: number;
    minute: number;
  } | null,
): string {
  if (!date) {
    return "дата неизвестна";
  }

  const day = String(date.day).padStart(2, "0");
  const month = String(date.month).padStart(2, "0");
  const year = String(date.year);
  const hour = String(date.hour).padStart(2, "0");
  const minute = String(date.minute).padStart(2, "0");

  return `${day}.${month}.${year} ${hour}:${minute}`;
}

function formatAuthor(entry: {
  actorLabel: string;
  userName: string | null;
  userRole: "admin" | "user" | "viewer" | null;
  userId: string | null;
}): string {
  const actorLabel = entry.actorLabel?.trim() || "";
  if (actorLabel) {
    return actorLabel;
  }

  const userName = entry.userName?.trim() || "";
  const userRole = entry.userRole?.trim() || "";
  const userId = entry.userId?.trim() || "";

  if (userName) {
    if (userRole) {
      return `${userName} (${userRole})`;
    }
    return userName;
  }

  if (userId) {
    return userId;
  }

  return "система";
}

export const JournalPage: React.FC = view(JournalPageViewModel)(({
  viewModel: {
    entries,
    actionFilter,
    productIdFilter,
    boxIdFilter,
    userIdFilter,
    userRoleFilter,
    limitFilter,
    hasActiveFilters,
    isLoading,
    error,
    onActionFilterChange,
    onProductIdFilterChange,
    onBoxIdFilterChange,
    onUserIdFilterChange,
    onUserRoleFilterChange,
    onLimitFilterChange,
    applyFilters,
    clearFilters,
    refresh,
    openEntry,
    openProduct,
    openBox,
  },
}) => {
  return (
    <Layout title={"журнал операций"}>
      <div className={styles.root}>
        <div className={styles.filters}>
          <FieldSet title={"действие"}>
            <select
              className={styles.select}
              value={actionFilter}
              onChange={onActionFilterChange}
            >
              <option value="all">все</option>
              <option value={Action.put}>put</option>
              <option value={Action.take}>take</option>
              <option value={Action.destroy}>destroy</option>
            </select>
          </FieldSet>

          <FieldSet title={"productId"}>
            <Input
              value={productIdFilter}
              onChange={onProductIdFilterChange}
              placeholder="prod_001"
            />
          </FieldSet>

          <FieldSet title={"boxId"}>
            <Input
              value={boxIdFilter}
              onChange={onBoxIdFilterChange}
              placeholder="box_001"
            />
          </FieldSet>

          <FieldSet title={"userId"}>
            <Input
              value={userIdFilter}
              onChange={onUserIdFilterChange}
              placeholder="usr_001"
            />
          </FieldSet>

          <FieldSet title={"роль пользователя"}>
            <select
              className={styles.select}
              value={userRoleFilter}
              onChange={onUserRoleFilterChange}
            >
              <option value="all">все роли</option>
              <option value="admin">admin</option>
              <option value="user">user</option>
              <option value="viewer">viewer</option>
            </select>
          </FieldSet>

          <FieldSet title={"limit"}>
            <Input value={limitFilter} onChange={onLimitFilterChange} />
          </FieldSet>

          <div className={styles.buttons}>
            <Button
              design={ButtonDesign.primary}
              onClick={() => void applyFilters()}
              disabled={isLoading}
            >
              применить
            </Button>
            <Button
              design={ButtonDesign.basic}
              onClick={clearFilters}
              disabled={isLoading || !hasActiveFilters}
            >
              сбросить
            </Button>
            <Button
              design={ButtonDesign.ghost}
              onClick={() => void refresh()}
              disabled={isLoading}
            >
              обновить
            </Button>
          </div>
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {isLoading && <p className={styles.hint}>загружаем журнал...</p>}

        {!isLoading && entries.length === 0 && (
          <p className={styles.hint}>записей по текущим фильтрам нет</p>
        )}

        {!isLoading && entries.length > 0 && (
          <ul className={styles.list}>
            {entries.map((entry) => (
              <li
                key={entry.id}
                className={styles.item}
                role="button"
                tabIndex={0}
                onClick={() => void openEntry(entry)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    void openEntry(entry);
                  }
                }}
              >
                <div className={styles.itemHeader}>
                  <strong>{entry.action}</strong>
                  <div className={styles.itemHeaderMeta}>
                    <span
                      className={
                        entry.actorType === "system"
                          ? `${styles.sourceBadge} ${styles.sourceSystem}`
                          : `${styles.sourceBadge} ${styles.sourceUser}`
                      }
                    >
                      {entry.actorType === "system"
                        ? "системное"
                        : "пользователь"}
                    </span>
                    <span>{formatDate(entry.date)}</span>
                  </div>
                </div>

                <p className={styles.meta}>product: {entry.productId}</p>
                <p className={styles.meta}>box: {entry.fromTo}</p>
                <p className={styles.meta}>value: {entry.value}</p>
                <p className={styles.meta}>
                  source:{" "}
                  {entry.actorType === "system"
                    ? "системное событие"
                    : "действие пользователя"}
                </p>
                <p className={styles.meta}>author: {formatAuthor(entry)}</p>

                <div className={styles.itemActions}>
                  <Button
                    design={ButtonDesign.basic}
                    onClick={(event) => {
                      event.stopPropagation();
                      void openProduct(entry);
                    }}
                  >
                    открыть продукт
                  </Button>
                  <Button
                    design={ButtonDesign.basicGray}
                    onClick={(event) => {
                      event.stopPropagation();
                      void openBox(entry);
                    }}
                  >
                    открыть коробку
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
});
